import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import { useUser } from "../../contexts/UserContext";

import { UPDATE_QUANTITATIVE_BENEFITS_BY_USER_ID } from "../../apollo-mutations";
import { GET_USER_QUANTITATIVE_BENEFITS_BY_ID } from "../../apollo-queries";

import { NavigationText, QuantitativeText } from "../../Text";
import { FaTrash } from "react-icons/fa";
import { BsArrowLeft, BsArrowRight } from "react-icons/bs";
import { MdAddCircle } from "react-icons/md";
import { CiCircleInfo } from "react-icons/ci";
import { Tooltip } from "@mui/material";
import { Button } from "@mui/base";

interface benefits {
  benefit_per_year: number;
  number_of_years: number;
  category: string;
  description: string;
  total_benefit: number;
  is_ongoing: boolean;
  benefits_per_month: number[];
  user_id: string;
}

const QuantitativeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  const { loading, error, data, refetch } = useQuery(
    GET_USER_QUANTITATIVE_BENEFITS_BY_ID,
    {
      variables: { user_id: user.uuid },
    }
  );

  const [
    updateBenefitsByUserId,
    { loading: updateLoading, error: updateError },
  ] = useMutation(UPDATE_QUANTITATIVE_BENEFITS_BY_USER_ID);

  // tracks the form data
  const { register, control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      benefits: data?.benefits || [
        {
          category: QuantitativeText.category.dropdown[0],
          benefit_per_year: 0,
          description: "",
          number_of_years: 1,
          benefits_per_month: [],
          is_ongoing: true,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "benefits",
  });

  // Convert a string[][] to a number[][]
  function convertToNumberArray(arr: string[][]): number[][] {
    return arr.map((innerArray) => {
      return innerArray.map((value) => {
        const numberValue = parseInt(value, 10); // Parse the value to an integer (base 10)
        return isNaN(numberValue) ? 0 : numberValue; // If the parsing fails, return 0
      });
    });
  }

  // Switching from ongoing to one time flattens the ongoing cost over one time months
  const calculateMonthlyBenefitsForItem = (benefit) => {
    const { benefit_per_year, number_of_years, is_ongoing } = benefit;

    if (is_ongoing) {
      let distributedBenefits = [];
      console.log("this is the benefit", benefit);
      const totalBenefit = benefit_per_year * number_of_years;
      const monthlyBenefit = totalBenefit / (number_of_years * 12);
      for (let i = 0; i < number_of_years; i++) {
        const monthlyBenefitArray = Array(12).fill(monthlyBenefit);
        // @ts-ignore
        distributedBenefits.push(monthlyBenefitArray);
      }
      return distributedBenefits;
    } else if (!is_ongoing) {
      //take the already existing benefits_per_month, and turn it into a number[][]
      const benefitsPerMonth = convertToNumberArray(benefit.benefits_per_month);
      console.log("benefits per month numberfied", benefitsPerMonth);
      return benefitsPerMonth;
    }
  };

  const formatBenefitsToSendToDB = (benefits) => {
    const formattedBenefits: benefits[] = [];
    benefits?.forEach((benefit) => {
      benefit.user_id = user.uuid;
      // if the benefit is ongoing, just use the cost per year and number of years to calculate the total cost
      if (benefit.is_ongoing) {
        benefit.total_benefit =
          benefit.benefit_per_year * benefit.number_of_years;
      } else {
        // if the benefit is not ongoing, then sum up the costs per month
        const flattenedBenefits = benefit?.benefits_per_month.flat();
        let total_benefit = flattenedBenefits.reduce(
          (acc, val) => acc + (parseFloat(val) || 0),
          0
        );
        benefit.total_benefit = total_benefit;
      }

      // format benefits_per_month
      benefit.benefits_per_month = calculateMonthlyBenefitsForItem(benefit);
      // remove typename from object to make graphql happy
      delete benefit.__typename;

      formattedBenefits.push(benefit);
    });
    return formattedBenefits;
  };

  //Category dropdown functions
  const handleSelectChange = (
    index: number,
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedValue = event.target.value;
    selectedValue === "Custom"
      ? setValue(`benefits.${index}.category`, "")
      : setValue(`benefits.${index}.category`, selectedValue); // Use the value from the dropdown
  };

  //called when pressing the cancel button to return to the dropdown input instead of the custom text input
  const handleCancelCustomCategory = (index: number) => {
    const dropdownLength = QuantitativeText.category.dropdown.length;
    const category =
      index >= dropdownLength - 1 // minus one to account for Custom
        ? QuantitativeText.category.dropdown[dropdownLength - 2] // Set to second last element if index is greater or equal to dropdown length excluding last element "Custom"
        : QuantitativeText.category.dropdown[index];

    setValue(`benefits.${index}.category`, category); // Clear the value in the form data
  };

  // resets the form data to the database data
  useEffect(() => {
    refetch();
    if (data && data.benefits.length > 0) {
      setValue(
        "benefits",
        data?.benefits || [
          {
            category: QuantitativeText.category.dropdown[0],
            benefit_per_year: 0,
            description: "",
            number_of_years: 1,
            is_ongoing: true,
            benefits_per_month: [],
          },
        ]
      );
    }
  }, [data, setValue, refetch]);

  // updates the database with the form data when the component unmounts
  useEffect(() => {
    return () => {
      console.log(data);
      const updatedFormData = watch();
      const formattedBenefits = formatBenefitsToSendToDB(
        updatedFormData?.benefits
      );
      updateBenefitsByUserId({
        variables: {
          user_id: user.uuid,
          benefits: formattedBenefits,
        },
      });
    };
  }, []);

  const handleNext = () => {
    navigate(`/Qualitative/${user.uuid}`);
  };

  const handlePrevious = () => {
    navigate(`/Costs/${user.uuid}`);
  };

  return (
    <>
      <form className="mt-6 flex flex-col space-y-4">
        {/* loop through fields array and render a form for each item */}
        {fields.map(
          (form, index) => {
            // Extract data from form using watch from form library
            const years = watch(`benefits.${index}.number_of_years`, 1);
            const isOngoing = watch(`benefits.${index}.is_ongoing`, true);
            const selectedCategory = watch(`benefits.${index}.category`);
            return (
              <li
                key={form.id}
                className="p-6 rounded-md bg-white drop-shadow-lg relative flex flex-col space-y-4"
              >
                {/* Cost Card Label */}
                <label className="text-2xl font-bold">
                  {QuantitativeText.card.label} {index + 1}
                </label>
                {/* Ongoing/One Time Toggle */}
                <div className="flex flex-col space-y-2 tablet:flex-row tablet:items-center tablet:space-y-0 tablet:space-x-4">
                  <div className="flex items-center">
                    <label className={`${isOngoing ? "text-UDig-orange" : ""}`}>
                      {QuantitativeText.toggle.ongoing.label}
                    </label>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      {...register(`benefits.${index}.is_ongoing`)}
                    />
                    <span className="slider round"></span>
                  </label>
                  <div className="flex items-center">
                    <label className={`${isOngoing ? "" : "text-UDig-orange"}`}>
                      {QuantitativeText.toggle.oneTime.label}
                    </label>
                    <Tooltip
                      title={
                        <>
                          <h3 className="font-bold">
                            {QuantitativeText.toggle.ongoing.label}
                          </h3>
                          <div>{QuantitativeText.toggle.ongoing.tooltip}</div>
                          <br />
                          <h3 className="font-bold">
                            {QuantitativeText.toggle.oneTime.label}
                          </h3>
                          <div>{QuantitativeText.toggle.oneTime.tooltip}</div>
                        </>
                      }
                      placement="right"
                      arrow
                    >
                      <Button>
                        <CiCircleInfo className="ml-2" />
                      </Button>
                    </Tooltip>
                  </div>
                </div>
                {/* Remove Button */}
                {fields.length === 1 ? null : (
                  <div className="absolute top-1 right-3">
                    <Tooltip
                      title={QuantitativeText.card.removeTooltip}
                      placement="left"
                      arrow
                    >
                      <Button
                        className="text-UDig-orange"
                        onClick={() => remove(index)}
                      >
                        <FaTrash className="ml-2" />
                      </Button>
                    </Tooltip>
                  </div>
                )}
                {/* Category and Descriptions */}
                <div className="flex">
                  <label className="font-semibold text-xl whitespace-nowrap">
                    {QuantitativeText.category.label}
                  </label>
                  <Tooltip
                    title={
                      <>
                        <h3 className="font-bold">
                          {QuantitativeText.category.label}
                        </h3>
                        <div>{QuantitativeText.category.tooltip}</div>
                      </>
                    }
                    placement="right"
                    arrow
                  >
                    <Button>
                      <CiCircleInfo className="mx-2 self-center" />
                    </Button>
                  </Tooltip>
                </div>
                {/* Dropdown custom field */}
                {selectedCategory === "Custom" ||
                !QuantitativeText.category.dropdown.includes(
                  selectedCategory
                ) ? ( // Conditionally render the text input for custom category
                  <div
                    className="flex space-x-2"
                    key={`custom-category-input-${index}`}
                  >
                    <input
                      type="text"
                      placeholder={QuantitativeText.category.placeholder}
                      value={selectedCategory}
                      {...register(`benefits.${index}.category`)}
                      className="w-full tablet:w-1/2 h-10 px-2 border border-light-gray rounded-md"
                    />
                    <Tooltip
                      title={QuantitativeText.category.cancel.tooltip}
                      placement="right"
                      arrow
                    >
                      <button
                        type="button"
                        onClick={() => handleCancelCustomCategory(index)}
                        className="px-2 py-1 rounded-md text-UDig-orange"
                      >
                        {QuantitativeText.category.cancel.label}
                      </button>
                    </Tooltip>
                  </div>
                ) : (
                  // Conditionally render the dropdown for non-custom category
                  <select
                    onChange={(event) => handleSelectChange(index, event)}
                    value={selectedCategory}
                    className={
                      "w-full tablet:w-1/2 h-10 px-1 border border-light-gray rounded-md"
                    }
                  >
                    {QuantitativeText.category.dropdown.map(
                      (categoryOption) => (
                        <option key={categoryOption} value={categoryOption}>
                          {categoryOption}
                        </option>
                      )
                    )}
                  </select>
                )}
                {/* Description */}
                <div className="flex">
                  <label className="font-semibold text-xl">
                    {QuantitativeText.description.label}
                  </label>
                  <Tooltip
                    title={
                      <>
                        <h3 className="font-bold">
                          {QuantitativeText.description.label}
                        </h3>
                        <div>{QuantitativeText.description.tooltip}</div>
                      </>
                    }
                    placement="right"
                    arrow
                  >
                    <Button>
                      <CiCircleInfo className="mx-2 self-center" />
                    </Button>
                  </Tooltip>
                </div>
                <textarea
                  placeholder={QuantitativeText.description.placeholder}
                  // @ts-ignore
                  {...register(`benefits.${index}.description`)}
                  className="w-full tablet:w-1/2 h-20 p-2 border border-light-gray rounded-md"
                />
                {/* checks whether or not the current form is for ongoing or not. Here is the ongoing portion */}
                {isOngoing ? (
                  <>
                    <div className="flex">
                      {/* Yearly Cost Field */}
                      <div className="flex items-center w-full tablet:w-1/2">
                        <label className="font-semibold text-xl whitespace-nowrap">
                          {QuantitativeText.yearlyBenefit.label}
                          <span className="text-red-500 font-bold ml-1">*</span>
                        </label>
                        <Tooltip
                          title={
                            <>
                              <h3 className="font-bold">
                                {QuantitativeText.yearlyBenefit.label}
                              </h3>
                              <div>
                                {QuantitativeText.yearlyBenefit.tooltip}
                              </div>
                            </>
                          }
                          className="tablet:hidden"
                          placement="top"
                          arrow
                        >
                          <Button>
                            <CiCircleInfo className="mx-2 tablet:mr-0 tablet:ml-2 self-center" />
                          </Button>
                        </Tooltip>
                        <div className="w-full relative tablet:ml-2">
                          <span className="absolute -left-1 top-1/2 transform -translate-y-1/2 pl-3 text-UDig-gray">
                            $
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="100000"
                            title=""
                            // @ts-ignore
                            {...register(`benefits.${index}.benefit_per_year`)}
                            className="w-full h-10 pl-5 border border-light-gray rounded-md"
                            required
                          />
                        </div>
                      </div>
                      <Tooltip
                        title={
                          <>
                            <h3 className="font-bold">
                              {QuantitativeText.yearlyBenefit.label}
                            </h3>
                            <div>{QuantitativeText.yearlyBenefit.tooltip}</div>
                          </>
                        }
                        className="hidden tablet:block"
                        placement="right"
                        arrow
                      >
                        <Button>
                          <CiCircleInfo className="mx-2 tablet:mr-0 tablet:ml-2 self-center" />
                        </Button>
                      </Tooltip>
                    </div>
                    <div className="flex">
                      {/* Number of years field */}
                      <div className="flex flex-nowrap items-center w-full tablet:w-1/2">
                        <label className="font-semibold text-xl whitespace-nowrap">
                          {QuantitativeText.numberOfYears.label}
                          <span className="text-red-500 font-bold ml-1">*</span>
                        </label>
                        <Tooltip
                          title={
                            <>
                              <h3 className="font-bold">
                                {QuantitativeText.numberOfYears.label}
                              </h3>
                              <div>
                                {QuantitativeText.numberOfYears.tooltip}
                              </div>
                            </>
                          }
                          className="tablet:hidden"
                          placement="top"
                          arrow
                        >
                          <Button>
                            <CiCircleInfo className="ml-2 self-center" />
                          </Button>
                        </Tooltip>
                        <input
                          type="number"
                          min="1"
                          max="5"
                          // @ts-ignore
                          {...register(`benefits.${index}.number_of_years`)}
                          className="w-full h-10 ml-2 px-2 border border-UDig-beige rounded-md"
                          required
                        />
                      </div>
                      <Tooltip
                        title={
                          <>
                            <h3 className="font-bold">
                              {QuantitativeText.numberOfYears.label}
                            </h3>
                            <div>{QuantitativeText.numberOfYears.tooltip}</div>
                          </>
                        }
                        className="hidden tablet:block"
                        placement="right"
                        arrow
                      >
                        <Button>
                          <CiCircleInfo className="ml-2 self-center" />
                        </Button>
                      </Tooltip>
                    </div>
                  </>
                ) : (
                  // Here is the one time portion
                  <>
                    <div className="flex">
                      {/* Number of Years */}
                      <div className="flex flex-nowrap items-center w-full tablet:w-1/2">
                        <label className="font-semibold text-xl whitespace-nowrap">
                          {QuantitativeText.numberOfYears.label}
                          <span className="text-red-500 font-bold ml-1">*</span>
                        </label>
                        <Tooltip
                          title={
                            <>
                              <h3 className="font-bold">
                                {QuantitativeText.numberOfYears.label}
                              </h3>
                              <div>
                                {QuantitativeText.numberOfYears.tooltip}
                              </div>
                            </>
                          }
                          className="tablet:hidden"
                          placement="top"
                          arrow
                        >
                          <Button>
                            <CiCircleInfo className="ml-2 self-center" />
                          </Button>
                        </Tooltip>
                        <input
                          type="number"
                          min="1"
                          max="5"
                          // @ts-ignore
                          {...register(`benefits.${index}.number_of_years`)}
                          className="w-full h-10 ml-2 px-2 border border-UDig-beige rounded-md"
                          required
                        />
                      </div>
                      <Tooltip
                        title={
                          <>
                            <h3 className="font-bold">
                              {QuantitativeText.numberOfYears.label}
                            </h3>
                            <div>{QuantitativeText.numberOfYears.tooltip}</div>
                          </>
                        }
                        className="hidden tablet:block"
                        placement="right"
                        arrow
                      >
                        <Button>
                          <CiCircleInfo className="ml-2 self-center" />
                        </Button>
                      </Tooltip>
                    </div>
                    {/* Monthly Costs */}
                    <div className="mt-2 flex flex-nowrap tablet:mt-0">
                      <label className="font-semibold text-xl whitespace-nowrap">
                        {QuantitativeText.monthlyBenefits.label}
                        <span className="text-red-500 font-bold ml-1">*</span>
                      </label>
                      <Tooltip
                        title={
                          <>
                            <h3 className="font-bold">
                              {QuantitativeText.monthlyBenefits.label}
                            </h3>
                            <div>
                              {QuantitativeText.monthlyBenefits.tooltip}
                            </div>
                          </>
                        }
                        placement="right"
                        arrow
                      >
                        <Button>
                          <CiCircleInfo className="mx-2 self-center" />
                        </Button>
                      </Tooltip>
                    </div>

                    {/* renders 12 months for each year that is inputted into the previous form */}
                    <div>
                      {Array.from({ length: years }, (_, yearIndex) => (
                        <>
                          <h3 className="mt-2 font-semibold">
                            Year {yearIndex + 1}
                          </h3>
                          <div className="grid grid-cols-3 gap-x-2 gap-y-1 tablet:grid-cols-4 laptop:grid-cols-6">
                            {Array.from({ length: 12 }, (_, monthIndex) => (
                              <div className="flex flex-col flex-nowrap">
                                <div className="mt-1 whitespace-nowrap">
                                  {`Month ${monthIndex + 1}`}
                                </div>
                                <div className="w-full relative">
                                  <span className="absolute -left-1 top-1/2 transform -translate-y-1/2 pl-3 text-UDig-gray">
                                    $
                                  </span>
                                  <input
                                    key={monthIndex}
                                    type="number"
                                    // Generate unique names for each month input
                                    step="100000"
                                    title=""
                                    min="0"
                                    {...register(
                                      `benefits.${index}.benefits_per_month.${yearIndex}.${monthIndex}`
                                    )}
                                    className="w-full h-10 pl-5 border border-light-gray rounded-md"
                                    required
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      ))}
                    </div>
                  </>
                )}
              </li>
            );
          } // end of map
        )}
      </form>
      {/* Navigation and Appending buttons */}
      <div className="mt-4 mb-20 flex justify-between text-UDig-orange">
        <Button
          className="flex items-center gap-x-2"
          type="button"
          onClick={() => {
            handlePrevious();
          }}
        >
          <BsArrowLeft />
          {NavigationText.previous}
        </Button>
        {/* New Benefit Button */}
        <Tooltip
          title={QuantitativeText.card.addCard.tooltip}
          placement="top"
          arrow
        >
          <Button
            className="flex items-center font-semibold"
            onClick={() => {
              const dropdownLength = QuantitativeText.category.dropdown.length;
              const category =
                fields.length >= dropdownLength - 1 // minus one to account for Custom
                  ? QuantitativeText.category.dropdown[dropdownLength - 2] // Set to second last element if index is greater or equal to dropdown length excluding last element "Custom"
                  : QuantitativeText.category.dropdown[fields.length]; // Modulate through the list otherwise
              append({
                category,
                benefit_per_year: 0,
                description: "",
                number_of_years: 1,
                is_ongoing: true,
                benefits_per_month: [],
              });
            }}
          >
            {QuantitativeText.card.addCard.label}
            <MdAddCircle className="ml-1" />
          </Button>
        </Tooltip>
        <Button
          className="flex items-center gap-x-2"
          type="button"
          onClick={() => handleNext()}
        >
          {NavigationText.next}
          <BsArrowRight />
        </Button>
      </div>
    </>
  );
};

export default QuantitativeScreen;
